import { AddCircle as PlusCircle } from '@mui/icons-material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CollaboratedNovel } from '@/lib/types';

import { useNovel } from '@/context/novel-context';
import { useUserNovels } from '@/context/user-novels-context';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { useExampleNovel } from '@/hooks/use-example-novel';

import { CreateNovelDialog } from './dashboard/CreateNovelDialog';
import { SubscriptionLimitGate } from './subscription/subscription-limit-gate';

interface NovelSelectionProps {
  showLabel?: boolean;
}

export function NovelSelection({ showLabel = true }: NovelSelectionProps) {
  const { t } = useTranslation();
  const { user } = useFirebase();
  const { selectedNovelId, setSelectedNovelId } = useNovel();
  const navigate = useNavigate();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const { userData } = useUserProfile();
  const collaboratedNovels = userData.collaboratedNovels;
  const { showExampleNovel, exampleNovelEntry } = useExampleNovel();

  const { ownedNovels: novels, isLoading } = useUserNovels();

  const allNovels = useMemo(() => {
    const list: Array<{ id: string; title: string; ownerId: string; role?: string }> = [];
    if (novels) {
      novels.forEach((n) => {
        list.push({ id: n.id, title: n.title, ownerId: user?.uid || '' });
      });
    }
    collaboratedNovels.forEach((collab: CollaboratedNovel) => {
      const key = `${collab.ownerId};${collab.novelId}`;
      list.push({
        id: key,
        title: collab.novelTitle,
        ownerId: collab.ownerId,
        role: collab.role,
      });
    });
    // Append the per-language example novel last
    if (showExampleNovel && exampleNovelEntry) {
      list.push({
        id: `${exampleNovelEntry.ownerId};${exampleNovelEntry.novelId}`,
        title: exampleNovelEntry.novelTitle || '',
        ownerId: exampleNovelEntry.ownerId,
        role: 'example',
      });
    }
    return list;
  }, [novels, collaboratedNovels, user?.uid, showExampleNovel, exampleNovelEntry]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
      <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
        {showLabel && (
          <InputLabel id="novel-select-label">
            {isLoading ? t('common.loading') : t('dashboard.selection_grid.title')}
          </InputLabel>
        )}
        <Select
          labelId="novel-select-label"
          id="novel-select"
          value={allNovels.some((n) => n.id === selectedNovelId) ? (selectedNovelId || '') : ''}
          label={showLabel ? (isLoading ? t('common.loading') : t('dashboard.selection_grid.title')) : undefined}
          onChange={(e) => {
            setSelectedNovelId(e.target.value);
            navigate({ to: '/' });
          }}
          disabled={isLoading || allNovels.length === 0}
          sx={{ borderRadius: 1.5 }}
        >
          {allNovels.map((novel) => {
            const roleSuffix =
              novel.role === 'collaborator' ? ' (Editor)'
                : novel.role === 'reader' ? ' (Viewer)'
                  : novel.role === 'example' ? ` (${t('example_novel.badge')})`
                    : '';
            return (
              <MenuItem key={novel.id} value={novel.id}>
                {novel.title}{roleSuffix}
              </MenuItem>
            );
          })}
          {allNovels.length === 0 && !isLoading && (
            <MenuItem disabled value="">{t('dashboard.selection_grid.empty_state')}</MenuItem>
          )}
        </Select>
      </FormControl>

      <SubscriptionLimitGate limit="ownedNovels" currentCount={novels.length}>
        <Tooltip title={t('dashboard.novel_card.create_new')}>
          <IconButton
            color="primary"
            onClick={() => setDialogOpen(true)}
            sx={{
              bgcolor: selectedNovelId ? 'transparent' : 'primary.main',
              color: selectedNovelId ? 'primary.main' : 'white',
              border: selectedNovelId ? '1px solid' : 'none',
              borderColor: selectedNovelId ? 'divider' : 'transparent',
              '&:hover': {
                bgcolor: selectedNovelId ? 'action.hover' : 'primary.dark',
              },
              width: 40,
              height: 40,
            }}
          >
            <PlusCircle sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </SubscriptionLimitGate>

      <CreateNovelDialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
